import { axiosJsonInstance } from "@/axios.instance";
import { DeviceNode } from "@/types/devices/DeviceNode";
import { AreaNode } from "@/types/groups/AreaNode";
import { AxiosInstance, AxiosRequestConfig } from "axios";
import { ServerDetails, DeviceChange, DeviceChanges, DeviceChangesResponse, DeviceConfiguration, ServerConfiguration, ServerConfigurationResponse, DeviceConfigurationSelections } from "@/store/devices/types";

class DevicesApi {
	private axios: AxiosInstance;

	constructor() {
		this.axios = axiosJsonInstance;
	}

	/**
	 * Gets the list of device nodes for a specified group in the group tree.
	 * @param groupId The ID of the group.
	 * @param deviceTypeId The type ID of the devices to get.
	 * @param regionUrl The URL of the region, or null.
	 * @returns An array of device nodes for the specified group.
	 */
	public async getDevicesForGroupTree(
		groupId: number,
		deviceTypeId: number,
		regionUrl: string = null
	): Promise<DeviceNode[]> {
		const { data } = await this.axios.get(`/Devices/Tree/${groupId}/${deviceTypeId}`, { regionUrl });
		return data;
	}

	/**
	 * Searches for devices of a specified device type with a query string.
	 * @param query The query string to search devices for.
	 * @param deviceTypeId The type ID's of the devices to include in the search.
	 * @param searchTermsInAnyOrder Whether or not to take the order of search terms (separated by spaces) into account.
	 * @param regionUrl The URL of the region, or null.
	 * @returns An object containing device nodes.
	 */
	public async searchDevicesForGroupTree(
		query: string,
		deviceTypeIds: number[],
		searchTermsInAnyOrder: boolean,
		regionUrl: string
	): Promise<DeviceNode[]> {
		const { data } = await this.axios.post(
			"/Devices/Tree/Search",
			{
				query,
				deviceTypeIds,
				searchTermsInAnyOrder
			},
			{ regionUrl }
		);
		return data;
	}

	public async applyChanges(
		changes: DeviceChanges
	): Promise<DeviceChangesResponse> {
		const { data } = await this.axios.post(
			"DeviceConfig/ApplyChanges",
			changes
		);
		return data;
	}

	// deviceConfiguration: DeviceConfiguration
    public async createConfig(
		serverConfig: ServerConfiguration
	): Promise<ServerConfigurationResponse> {
		const { data } = await this.axios.post(
			"DeviceConfig/Add",
			serverConfig
		);
		return data;
	}

	public async reGetConfig(
		serverConfigAdded: ServerConfiguration
	): Promise<DeviceChange[]> {
		const { data } = await this.axios.post(
			"DeviceConfig/GetChanges",
			serverConfigAdded
		);
		return data;
	}

	public async getConfig(
		file: string,
		type: string,
		urlTemplate: string,
		prefValue: string,
		host: string,
		port: number,
		user: string,
		password: string,
		extra: string,
		input1: string,
		input2: string,
		deviceExtra: string,
		site: string,
		format: string,
		auth: string
		): Promise<any> {
		let urlBase = window.location.origin;
		const { data } = await this.axios.get(
			`${urlBase}/devices/config?file=${file}&type=${type}&urlTemplate=${urlTemplate}&prefValue=${prefValue}&host=${host}&port=${port}&user=${user}&password=${password}&extra=${extra}&input1=${input1}&input2=${input2}&deviceExtra=${deviceExtra}&site=${site}&format=${format}&auth=${auth}`);
		return data;
	}
}

const devicesApi = new DevicesApi();
export { devicesApi };
